import { useRef, useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import API_ROUTES from "../../../api-routes";
import apiService from "../../../services/service";
import useDateTime from "../../../hooks/useDateTime";
import { useDispatch, useSelector } from "react-redux";
import { z } from "zod";
import { updateConfig } from "~/Pages/Login/loginSlice";
import useFileUpload from "~/utils/useFileUpload";
import {
  getPeriodFromFileYearMonth,
  getAttachmentVersionKey,
} from "~/utils/fileUtils";
import Categories from "~/components/Processes/conversations/utils/categories.json";
import { fetchMilestones } from "../actions/milestoneSlice";

function useMessages({
  fetchMessages,
  relatedType,
  requestType,
  serviceData = {},
  allConversationAttachments = [],
}) {
  const { id } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const commentId = searchParams.get("commentId");
  const commentType = searchParams.get("commentType");
  const { formatDateTime } = useDateTime();
  const dispatch = useDispatch();
  const { processdata } = useSelector((state) => state.process);
  const allServices = useSelector(
    (state) => state.globalEntities.entityservices,
  );

  const fileSupported = [
    "xls",
    "csv",
    "xml",
    "pdf",
    "jpg",
    "jpeg",
    "png",
    "txt",
    "doc",
    "docx",
    "xlsx",
  ];

  const [isMessageLoading, setIsMessageLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteCommentModal, setDeleteCommentModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [messageText, setMessageText] = useState("");
  const [charCounter, setCharCounter] = useState(0);
  const [files, setFiles] = useState([]);
  const inputTextAreaRef = useRef(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [toggleNotification, setToggleNotification] = useState(false);
  const [isDataValid, setIsDataValid] = useState(false);
  const [isMessageSent, setIsMessageSent] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");

  const isPublic = location.pathname.includes("public");
  const { config, id: userId } = useSelector((state) => state.login);
  const [showAttachmentConfirmation, setShowAttachmentConfirmation] =
    useState(false);

  // Bidirectional pagination states
  const [prevPage, setPrevPage] = useState(1);
  const [nextPage, setNextPage] = useState(1);
  const [data, setData] = useState([]);
  const [hasMorePrevData, setHasMorePrevData] = useState(true);
  const [hasMoreNextData, setHasMoreNextData] = useState(true);
  const [isDataFetching, setDataFetching] = useState(false);
  const [firstMessageId, setFirstMessageId] = useState(null);
  const [lastMessageId, setLastMessageId] = useState(null);

  const PAGE_SIZE = 4;

  const {
    uploadFile,
    updateTag,
    ulidToUuid,
    uuidToUlid,
    SERVICE_CODES,
    getFileVersion,
    REQUEST_REASON_CODES,
    getAdHocEscalationDisplayName,
  } = useFileUpload();

  const getSubCategoryName = (categoryCode, subCategoryCode) => {
    const cat = Categories?.find((c) => c.code === categoryCode);
    const sub = cat?.subCategory?.find((s) => s.code === subCategoryCode);
    return sub?.name ?? subCategoryCode ?? undefined;
  };

  /**
   * Enriches file objects with versionNumber so that same subcategory (and period)
   * increments across existing conversation attachments and the current batch.
   */
  const enrichFilesWithVersionNumbers = (filesToSend) => {
    const existing = allConversationAttachments || [];
    return filesToSend.map((file) => {
      const key = getAttachmentVersionKey(file);
      const existingCount = existing.filter(
        (a) => getAttachmentVersionKey(a) === key,
      ).length;
      const sameKeyInBatch = filesToSend.filter(
        (f) => getAttachmentVersionKey(f) === key,
      );
      const indexInBatch = sameKeyInBatch.indexOf(file) + 1;
      const versionNumber = existingCount + indexInBatch;
      file.versionNumber = versionNumber;
      return file;
    });
  };

  const fetchPublicMessages = async (
    fetchDataType = "INITIAL",
    skipCache = false,
  ) => {
    if (isDataFetching) {
      return { data: [] };
    }
    try {
      setDataFetching(true);
      if (fetchDataType === "INITIAL") {
        setIsMessageLoading(true);
      }

      let currentPage;
      if (fetchDataType === "FETCHPREVDATA") {
        currentPage = prevPage;
      } else if (fetchDataType === "FETCHNEXTDATA") {
        currentPage = nextPage;
      } else {
        currentPage = 1;
      }

      // Build params
      const replaceParams = {
        relatedId: id,
        isPublic: true,
        relatedType: "PROCESS",
        page: currentPage,
        fetchDataType: fetchDataType,
      };

      // Add commentId and commentType if they exist
      if (commentId) {
        replaceParams.commentId = commentId;
      }
      if (commentType) {
        replaceParams.commentType = commentType;
      }

      // For PREV (older): Send the OLDEST message ID we currently have
      if (fetchDataType === "FETCHPREVDATA" && firstMessageId) {
        replaceParams.beforeMessageId = firstMessageId;
        // console.log("Getting messages OLDER than:", firstMessageId);
      }

      // For NEXT (newer): Send the NEWEST message ID we currently have
      else if (fetchDataType === "FETCHNEXTDATA" && lastMessageId) {
        replaceParams.afterMessageId = lastMessageId;
        // console.log("Getting messages NEWER than:", lastMessageId);
      }

      const response = await apiService(API_ROUTES.FETCH_CONVERSATIONS, "GET", {
        replace: replaceParams,
        schema: z.object({
          relatedId: z.string().uuid(),
          relatedType: z.string(),
          isPublic: z.boolean().optional(),
        }),
        // GXM-1754: Bypass APIM 60s cache after mutations
        skipCache,
      });

      if (response?.data?.length > 0) {
        if (fetchDataType === "FETCHPREVDATA") {
          // PREPEND older messages WITH DEDUPLICATION
          const currentIds = new Set(data.map((msg) => msg.id));
          const uniqueNewMessages = response.data.filter(
            (msg) => !currentIds.has(msg.id),
          );

          if (uniqueNewMessages.length > 0) {
            const newData = [...uniqueNewMessages, ...data];

            // Sort by date to ensure chronological order (oldest first)
            const sortedData = newData.sort(
              (a, b) =>
                new Date(a.createdAt || a.created_at) -
                new Date(b.createdAt || b.created_at),
            );

            setData(sortedData);

            // Update firstMessageId to the OLDEST message
            const newFirstId = sortedData[0]?.id;
            if (newFirstId) {
              setFirstMessageId(newFirstId);
              // console.log(`Updated firstMessageId (oldest): ${newFirstId}`);
            }
          } else {
            setHasMorePrevData(false);
          }

          setPrevPage((prev) => prev + 1);

          if (response.data.length < PAGE_SIZE) {
            setHasMorePrevData(false);
            // console.log(`No more prev data`);
          }
        } else if (fetchDataType === "FETCHNEXTDATA") {
          // APPEND newer messages WITH DEDUPLICATION
          const currentIds = new Set(data.map((msg) => msg.id));
          const uniqueNewMessages = response.data.filter(
            (msg) => !currentIds.has(msg.id),
          );

          if (uniqueNewMessages.length > 0) {
            const newData = [...data, ...uniqueNewMessages];

            // Sort by date to ensure chronological order (oldest first)
            const sortedData = newData.sort(
              (a, b) =>
                new Date(a.createdAt || a.created_at) -
                new Date(b.createdAt || b.created_at),
            );

            setData(sortedData);

            // Update lastMessageId to the NEWEST message
            const newLastId = sortedData[sortedData.length - 1]?.id;
            if (newLastId) {
              setLastMessageId(newLastId);
            }
          } else {
            setHasMoreNextData(false);
          }

          setNextPage((prev) => prev + 1);

          if (response.data.length < PAGE_SIZE) {
            setHasMoreNextData(false);
          }
        } else {
          // INITIAL load
          // Sort by date to ensure chronological order
          const sortedData = response.data.sort(
            (a, b) =>
              new Date(a.createdAt || a.created_at) -
              new Date(b.createdAt || b.created_at),
          );

          setData(sortedData);

          // Set BOTH boundary IDs from sorted data
          if (sortedData.length > 0) {
            const newFirstId = sortedData[0]?.id; // Oldest
            const newLastId = sortedData[sortedData.length - 1]?.id; // Newest

            setFirstMessageId(newFirstId);
            setLastMessageId(newLastId);

            //Can be used for debugging later if issue comes
            // console.log(`INITIAL load: ${sortedData.length} messages`);
            // console.log(`firstMessageId (oldest): ${newFirstId}`);
            // console.log(`lastMessageId (newest): ${newLastId}`);
          }

          setPrevPage(2);
          setNextPage(2);
          setHasMorePrevData(true);
          setHasMoreNextData(true);
        }
      } else {
        if (fetchDataType === "FETCHPREVDATA") {
          setHasMorePrevData(false);
        } else if (fetchDataType === "FETCHNEXTDATA") {
          setHasMoreNextData(false);
        }
      }

      setIsMessageLoading(false);
      setDataFetching(false);
      return response;
    } catch (error) {
      setIsMessageLoading(false);
      setDataFetching(false);
      return { data: [] };
    }
  };

  const fetchInternalMessages = async (
    fetchDataType = "INITIAL",
    skipCache = false,
  ) => {
    if (isDataFetching) {
      return { data: [] };
    }

    try {
      setDataFetching(true);
      if (fetchDataType === "INITIAL") {
        setIsMessageLoading(true);
      }

      let currentPage;
      if (fetchDataType === "FETCHPREVDATA") {
        currentPage = prevPage;
      } else if (fetchDataType === "FETCHNEXTDATA") {
        currentPage = nextPage;
      } else {
        currentPage = 1;
      }

      const replaceParams = {
        relatedId: id,
        isPublic: false,
        relatedType: "PROCESS",
        page: currentPage,
        fetchDataType: fetchDataType,
      };

      if (commentId) {
        replaceParams.commentId = commentId;
      }
      if (commentType) {
        replaceParams.commentType = commentType;
      }

      if (fetchDataType === "FETCHPREVDATA" && firstMessageId) {
        replaceParams.beforeMessageId = firstMessageId;
      } else if (fetchDataType === "FETCHNEXTDATA" && lastMessageId) {
        replaceParams.afterMessageId = lastMessageId;
      }

      const response = await apiService(API_ROUTES.FETCH_CONVERSATIONS, "GET", {
        replace: replaceParams,
        schema: z.object({
          relatedId: z.string().uuid(),
          relatedType: z.string(),
          isPublic: z.boolean().optional(),
        }),
        // GXM-1754: Bypass APIM 60s cache after mutations
        skipCache,
      });

      if (response?.data?.length > 0) {
        if (fetchDataType === "FETCHPREVDATA") {
          // PREPEND older messages WITH DEDUPLICATION
          const currentIds = new Set(data.map((msg) => msg.id));
          const uniqueNewMessages = response.data.filter(
            (msg) => !currentIds.has(msg.id),
          );

          if (uniqueNewMessages.length > 0) {
            const newData = [...uniqueNewMessages, ...data];

            // Sort by date to ensure chronological order (oldest first)
            const sortedData = newData.sort(
              (a, b) =>
                new Date(a.createdAt || a.created_at) -
                new Date(b.createdAt || b.created_at),
            );

            setData(sortedData);

            // Update firstMessageId to the OLDEST message
            const newFirstId = sortedData[0]?.id;
            if (newFirstId) {
              setFirstMessageId(newFirstId);
              //console.log(`Updated firstMessageId (Internal): ${newFirstId}`);
            }
          } else {
            setHasMorePrevData(false);
          }

          setPrevPage((prev) => prev + 1);

          if (response.data.length < PAGE_SIZE) {
            setHasMorePrevData(false);
          }
        } else if (fetchDataType === "FETCHNEXTDATA") {
          // APPEND newer messages WITH DEDUPLICATION
          const currentIds = new Set(data.map((msg) => msg.id));
          const uniqueNewMessages = response.data.filter(
            (msg) => !currentIds.has(msg.id),
          );

          if (uniqueNewMessages.length > 0) {
            const newData = [...data, ...uniqueNewMessages];

            // Sort by date to ensure chronological order (oldest first)
            const sortedData = newData.sort(
              (a, b) =>
                new Date(a.createdAt || a.created_at) -
                new Date(b.createdAt || b.created_at),
            );

            setData(sortedData);

            // Update lastMessageId to the NEWEST message
            const newLastId = sortedData[sortedData.length - 1]?.id;
            if (newLastId) {
              setLastMessageId(newLastId);
            }
          } else {
            setHasMoreNextData(false);
          }

          setNextPage((prev) => prev + 1);

          if (response.data.length < PAGE_SIZE) {
            setHasMoreNextData(false);
          }
        } else {
          // INITIAL load
          // Sort by date to ensure chronological order
          const sortedData = response.data.sort(
            (a, b) =>
              new Date(a.createdAt || a.created_at) -
              new Date(b.createdAt || b.created_at),
          );

          setData(sortedData);

          // Set BOTH boundary IDs from sorted data
          if (sortedData.length > 0) {
            const newFirstId = sortedData[0]?.id; // Oldest
            const newLastId = sortedData[sortedData.length - 1]?.id; // Newest

            setFirstMessageId(newFirstId);
            setLastMessageId(newLastId);
            //San be used later for debugging purpose
            // console.log(`INITIAL load (Internal): ${sortedData.length} messages`);
            // console.log(`firstMessageId (oldest) (Internal): ${newFirstId}`);
            // console.log(`lastMessageId (newest) (Internal): ${newLastId}`);
          }

          setPrevPage(2);
          setNextPage(2);
          setHasMorePrevData(true);
          setHasMoreNextData(true);
        }
      } else {
        if (fetchDataType === "FETCHPREVDATA") {
          setHasMorePrevData(false);
        } else if (fetchDataType === "FETCHNEXTDATA") {
          setHasMoreNextData(false);
        }
      }

      setIsMessageLoading(false);
      setDataFetching(false);
      return response;
    } catch (error) {
      setIsMessageLoading(false);
      setDataFetching(false);
      return { data: [] };
    }
  };

  const resetPaginationState = () => {
    setPrevPage(1);
    setNextPage(1);
    setData([]);
    setHasMorePrevData(true);
    setHasMoreNextData(true);
    setDataFetching(false);
  };

  const deleteConversation = async (messageId) => {
    await apiService(API_ROUTES.DELETE_CONVERSATION, "DELETE", {
      replace: { id: messageId, isPublic: false },
      schema: z.object({ id: z.string().uuid() }),
    });
    dispatch(fetchMilestones({ id, skipCache: true }));
  };

  const restoreConversation = async (messageId) => {
    await apiService(API_ROUTES.RESTORE_CONVERSATION, "POST", {
      replace: { id: messageId },
      schema: z.object({ id: z.string().uuid() }),
    });
    dispatch(fetchMilestones({ id, skipCache: true }));
  };

  const addMessages = async (data, relatedType, requestType = "") => {
    const attachments = [];
    let service, tags;

    if (relatedType === "REQUEST") {
      tags = {
        entity_id: uuidToUlid(data.entitydata.id),
        service: SERVICE_CODES[data?.serviceData?.name],
        country: data.entitydata.country,
        request: uuidToUlid(id),
        request_reason: REQUEST_REASON_CODES[data?.requestdata?.reason],
        year: new Date().getFullYear().toString(),
      };
    } else {
      service = getService();
      tags = {
        entity_id: uuidToUlid(processdata?.entity),
        service: SERVICE_CODES[service.name],
        is_public: data.isPublic === true ? "true" : "false",
        country: processdata?.entityData.country,
        process: uuidToUlid(id),
        year: new Date().getFullYear().toString(),
      };
    }

    for (let i = 0; i < data.files.length; i++) {
      const file = data.files[i];
      const subCategoryCode =
        typeof file?.subcategory === "object"
          ? file.subcategory?.code
          : file?.subcategory;

      const tagData = {
        ...tags,
        category: file?.category,
        subCategory: subCategoryCode,
        temporary_file: "false",
        ...(getPeriodFromFileYearMonth(file?.fileYearMonth) && {
          period: getPeriodFromFileYearMonth(file.fileYearMonth),
        }),
      };
      let version =
        file?.versionNumber?.toString() || `${await getFileVersion(tagData)}`;
      const metadata = {
        original_file_name: encodeURIComponent(file.fileName || file.name),
        file_size: file.size.toString(),
        file_type: file.type,
        user: userId,
        file_name: file.fileName,
        version,
        created_at: new Date().toISOString(),
        file_object_type: relatedType === "REQUEST" ? "AD_HOC" : "PROCESS",
        ...(file?.description && {
          description: encodeURIComponent(file?.description),
        }),
      };

      if (relatedType === "REQUEST") {
        let subCategoryName;
        if (typeof file?.subcategory === "object") {
          subCategoryName = file.subcategory?.name;
        } else {
          subCategoryName = getSubCategoryName(
            file?.category,
            file?.subcategory,
          );
        }
        if (subCategoryName) {
          let displayDate = null;
          if (file?.fileYearMonth?.year && file?.fileYearMonth?.month) {
            displayDate = new Date(
              file.fileYearMonth.year,
              file.fileYearMonth.month - 1,
              1,
            );
          }
          metadata.display_name = getAdHocEscalationDisplayName(
            subCategoryName,
            displayDate,
            file.fileName || file.name,
          );
        }
      } else if (relatedType === "PROCESS") {
        let subCategoryName;
        if (typeof file?.subcategory === "object") {
          subCategoryName = file.subcategory?.name;
        } else if (file?.subcategoryName) {
          subCategoryName = file.subcategoryName;
        } else if (file?.subcategory) {
          subCategoryName = getSubCategoryName(
            file?.category,
            file?.subcategory,
          );
        }

        if (subCategoryName) {
          let displayDate = null;
          if (file?.fileYearMonth?.year && file?.fileYearMonth?.month) {
            displayDate = new Date(
              file.fileYearMonth.year,
              file.fileYearMonth.month - 1,
              1,
            );
          }
          metadata.display_name = getAdHocEscalationDisplayName(
            subCategoryName,
            displayDate,
            file.fileName || file.name,
          );
        }
      }

      const scopeEntityId =
        relatedType === "REQUEST" ? data.entitydata.id : processdata?.entity;
      const scopeServiceCode =
        relatedType === "REQUEST"
          ? SERVICE_CODES[data?.serviceData?.name]
          : SERVICE_CODES[getService()?.name];
      const uploadData = await uploadFile(file, file.fileName, {
        entityId: scopeEntityId,
        serviceLineCode: scopeServiceCode,
        fileName: file.fileName,
      });
      attachments.push({ url: uploadData.url, fileName: uploadData.id });
      await updateTag(uploadData.id, tagData, {
        ...metadata,
        url: uploadData.url,
      });
    }

    return await apiService(API_ROUTES.ADD_MESSAGES, "POST", {
      body: JSON.stringify({
        comment: data.comment,
        service:
          relatedType === "REQUEST"
            ? data?.serviceData?.id
            : processdata?.service,
        isPublic: data.isPublic,
        attachments,
      }),
      replace: {
        relatedId: id,
        relatedType: relatedType === "REQUEST" ? requestType : relatedType,
      },
      schema: z.object({
        relatedId: z.string().uuid(),
        relatedType: z.enum(["AD_HOC", "ESCALATION", "PROCESS"]),
      }),
    });
  };

  const getService = () => {
    return allServices.find((service) => service?.id === processdata?.service);
  };

  const disableNotification = async () => {
    return await apiService(API_ROUTES.DISABLE_NOTIFICATION, "PUT", {
      body: { process: id },
      schema: z.object({ process: z.string().uuid() }),
    });
  };

  const formatDate = (dateString) => {
    let formattedNewDate = formatDateTime({
      date: dateString,
      dateformat: "MMMM do, yyyy 'at' H:mm",
    });
    return formattedNewDate;
  };

  const sendMessage = async () => {
    setIsMessageLoading(true);
    const fileDate = files.map((file) => file.file);
    const filesWithVersions = enrichFilesWithVersionNumbers(fileDate);
    const data = {
      isPublic,
      service: null,
      attachments: null,
      comment: messageText,
      parent: null,
      files: filesWithVersions,
    };

    try {
      setIsMessageSent(true);
      const promises = [addMessages(data, relatedType)];
      if (
        toggleNotification &&
        !config?.disable_notification_public_message_process?.includes(id)
      ) {
        promises.push(disableNotification());
      }
      const [_, notificationResponse] = await Promise.all(promises);
      if (notificationResponse?.data) {
        dispatch(updateConfig(notificationResponse?.data?.config));
      }

      if (fetchMessages) {
        await fetchMessages("INITIAL", true);
      } else {
        resetPaginationState();
        if (isPublic) {
          await fetchPublicMessages("INITIAL", true);
        } else {
          await fetchInternalMessages("INITIAL", true);
        }
      }

      setMessageText("");
      setCharCounter(0);
      setIsMessageLoading(false);
      setFiles([]);
      if (inputTextAreaRef.current) {
        inputTextAreaRef.current.style.height = "44px";
      }
    } catch (error) {
      setIsMessageLoading(false);
      setShowConfirmDialog(false);
      setIsMessageSent(false);
    }
  };

  const sendHandler = async () => {
    setIsMessageLoading(true);
    if (isPublic) {
      if (config?.disable_notification_public_message_process?.includes(id)) {
        await sendMessage();
      } else {
        setShowConfirmDialog(true);
        setToggleNotification(false);
      }
    } else {
      await sendMessage();
    }
    setIsMessageLoading(false);
  };

  const sendRequestHandle = async (entitydata, requestdata) => {
    setIsMessageLoading(true);
    const fileDate = files.map((file) => file.file);
    const filesWithVersions = enrichFilesWithVersionNumbers(fileDate);
    const data = {
      isPublic,
      comment: messageText,
      files: filesWithVersions,
      serviceData,
      entitydata,
      requestdata,
    };

    try {
      setIsMessageSent(true);
      await addMessages(data, relatedType, requestType);

      if (fetchMessages) {
        await fetchMessages("INITIAL", true);
      } else {
        resetPaginationState();
        if (isPublic) {
          await fetchPublicMessages(requestType, true);
        } else {
          await fetchInternalMessages(requestType, true);
        }
      }

      setMessageText("");
      setCharCounter(0);
      setIsMessageLoading(false);
      setFiles([]);
    } catch (error) {
      setIsMessageLoading(false);
      setShowConfirmDialog(false);
      setIsMessageSent(false);
    }
  };

  const send = (entitydata, requestdata) => {
    setShowAttachmentConfirmation(false);
    if (relatedType === "REQUEST") {
      sendRequestHandle(entitydata, requestdata);
    } else {
      sendHandler();
    }
  };

  const submitPayslipMatchingResult = async (matchedFiles) => {
    try {
      return await apiService(
        API_ROUTES.UPDATE_PAYSLIP_MATCHING_RESULTS,
        "PATCH",
        {
          body: { matchedFiles, updateMessage: true },
        },
      );
    } catch (error) {
      console.error("Error submitting payslip matching result:", error);
    }
  };

  const addPayslipSubmittedMessage = async (attachments) => {
    try {
      const data = await apiService(
        API_ROUTES.ADD_PAYSLIP_PROCESSING_MESSAGE,
        "POST",
        {
          body: {
            comment:
              "You're all set! If there are no more files to add, go ahead and mark the Submit Final Output task as complete.",
            isPublic: false,
            attachments,
            type: "PAYSLIP_SUBMITTED",
          },
          replace: {
            relatedId: id,
            relatedType: "PROCESS",
          },
          schema: z.object({
            relatedId: z.string().uuid(),
            relatedType: z.enum(["PROCESS"]),
            comment: z.string(),
            isPublic: z.boolean(),
            attachments: z.array(
              z.object({
                fileName: z.string(),
                url: z.string(),
              }),
            ),
          }),
        },
      );

      if (fetchMessages) {
        await fetchMessages("INITIAL", true);
      } else {
        resetPaginationState();
        await fetchInternalMessages("INITIAL", true);
      }

      return data;
    } catch (error) {
      console.error("Error adding payslip processing message:", error);
      throw error;
    }
  };

  const manualMatchPayslips = async (matchingResultId, employeeId) => {
    try {
      return await apiService(API_ROUTES.MANUAL_MATCH_PAYSLIPS, "POST", {
        body: {
          payslip_result_id: matchingResultId,
          employee_profile_id: employeeId,
        },
        schema: z.object({
          payslip_result_id: z.string().uuid(),
          employee_profile_id: z.string().uuid(),
        }),
      });
    } catch (error) {
      console.error("Error initiating manual payslip matching:", error);
    }
  };

  return {
    fetchPublicMessages,
    fetchInternalMessages,
    deleteConversation,
    addMessages,
    isPublic,
    isMessageLoading,
    setIsMessageLoading,
    formatDate,
    disableNotification,
    fileSupported,
    config,
    getService,
    deleteLoading,
    setDeleteLoading,
    deleteModal,
    setDeleteModal,
    selectedMessage,
    setSelectedMessage,
    processdata,
    messageText,
    setMessageText,
    charCounter,
    setCharCounter,
    showConfirmDialog,
    setShowConfirmDialog,
    isDataValid,
    setIsDataValid,
    isMessageSent,
    setIsMessageSent,
    showUploadModal,
    setShowUploadModal,
    files,
    setFiles,
    sendMessage,
    inputTextAreaRef,
    toggleNotification,
    setToggleNotification,
    send,
    deleteCommentModal,
    setDeleteCommentModal,
    restoreConversation,
    notificationMessage,
    setNotificationMessage,
    showAttachmentConfirmation,
    setShowAttachmentConfirmation,
    submitPayslipMatchingResult,
    addPayslipSubmittedMessage,
    hasMorePrevData,
    hasMoreNextData,
    prevPage,
    nextPage,
    setPrevPage,
    setNextPage,
    firstMessageId,
    lastMessageId,
    setFirstMessageId,
    setLastMessageId,
    setHasMorePrevData,
    setHasMoreNextData,
    data,
    setData,
    isDataFetching,
    setDataFetching,
    resetPaginationState,
    manualMatchPayslips,
  };
}

export default useMessages;
